import { z } from 'zod';

// ---------------------------------------------------------------------------
// Payment services
// ---------------------------------------------------------------------------

/**
 * Rails a payment can travel on (#66). The list is open — SEPA and CHIPS are
 * expected — so nothing here is exhaustive: the screens key off whatever codes
 * the API returns and only use these constants for presentation.
 */
export const PAYMENT_SERVICE = {
  SWIFT: 'SWIFT',
  INTERNAL: 'INTERNAL',
} as const;

export const PAYMENT_SERVICE_LABELS: Record<string, string> = {
  [PAYMENT_SERVICE.SWIFT]: 'SWIFT',
  [PAYMENT_SERVICE.INTERNAL]: 'Internal transfer',
};

export function paymentServiceLabel(code: string): string {
  return PAYMENT_SERVICE_LABELS[code] ?? code;
}

// ---------------------------------------------------------------------------
// Pricing list item — one row per (client, rail)
// ---------------------------------------------------------------------------

const feeString = z.string();

export const ClientPricingListItemSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  tenant_cod: z.string(),
  company_name: z.string(),
  payment_service_code: z.string(),
  outgoing_fee_fixed_value: feeString,
  outgoing_fee_percent_value: feeString,
  incoming_fee_fixed_value: feeString,
  incoming_fee_percent_value: feeString,
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type ClientPricingListItem = z.infer<typeof ClientPricingListItemSchema>;

// ---------------------------------------------------------------------------
// Paginated list response
// ---------------------------------------------------------------------------

export const ClientPricingListResponseSchema = z.object({
  items: z.array(ClientPricingListItemSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
});

export type ClientPricingListResponse = z.infer<typeof ClientPricingListResponseSchema>;

// ---------------------------------------------------------------------------
// Single client pricing — every rail plus the client-level spread
// ---------------------------------------------------------------------------

export const ServicePricingSchema = z.object({
  payment_service_id: z.number(),
  payment_service_code: z.string(),
  outgoing_fee_fixed_value: feeString,
  outgoing_fee_percent_value: feeString,
  incoming_fee_fixed_value: feeString,
  incoming_fee_percent_value: feeString,
  is_active: z.boolean(),
  updated_at: z.string(),
});

export type ServicePricing = z.infer<typeof ServicePricingSchema>;

/**
 * `services` may be empty — a client charged nothing is a legitimate
 * arrangement, not a missing record. `fx_spread` sits alongside the list
 * because it applies to any conversion whichever rail the funds then take.
 */
export const ClientPricingSchema = z.object({
  client_id: z.number(),
  tenant_cod: z.string(),
  company_name: z.string(),
  fx_spread: feeString,
  services: z.array(ServicePricingSchema),
});

export type ClientPricingDetail = z.infer<typeof ClientPricingSchema>;

// ---------------------------------------------------------------------------
// Pricing update request — one rail per call
// ---------------------------------------------------------------------------

const DECIMAL_6DP = /^\d+(\.\d{1,6})?$/;
const MAX_FEE = 999999.999999;

function feeField(label: string) {
  return z
    .string()
    .regex(DECIMAL_6DP, `${label} must be numeric with up to 6 decimals`)
    .refine((val) => {
      const n = parseFloat(val);
      return n >= 0 && n <= MAX_FEE;
    }, `${label} must be between 0 and ${MAX_FEE}`);
}

function percentField(label: string) {
  return z
    .string()
    .regex(DECIMAL_6DP, `${label} must be numeric with up to 6 decimals`)
    .refine((val) => {
      const n = parseFloat(val);
      return n >= 0 && n <= 100;
    }, `${label} must be between 0 and 100`);
}

/**
 * The PUT body sets one rail's four fees. Fees are cumulative — a fixed part
 * plus a percentage — so all four are always sent, and a zero is a real term
 * rather than an omission.
 */
export const pricingUpdateSchema = z.object({
  payment_service_code: z.string().min(1),
  outgoing_fee_fixed_value: feeField('Outgoing fixed fee'),
  outgoing_fee_percent_value: percentField('Outgoing percentage fee'),
  incoming_fee_fixed_value: feeField('Incoming fixed fee'),
  incoming_fee_percent_value: percentField('Incoming percentage fee'),
});

export type PricingUpdateRequest = z.infer<typeof pricingUpdateSchema>;

// ---------------------------------------------------------------------------
// Filter / query state
// ---------------------------------------------------------------------------

export interface PricingListFilters {
  search: string;
  sort_by: string;
  sort_order: 'asc' | 'desc';
  page: number;
  page_size: number;
}

export const DEFAULT_PRICING_FILTERS: PricingListFilters = {
  search: '',
  sort_by: 'company_name',
  sort_order: 'asc',
  page: 1,
  page_size: 20,
};
